import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { LoadingButton } from "@mui/lab";

import { Strings } from "@app/constants";
import { useRequestState } from "@app/hooks";
import { assignCustomerToSalesPerson, customer, salesPerson } from "../slice";
import {
  selectAssignCustomerToSalesPerson,
  selectCustomerState,
} from "../selector";
import { AddSalespersonModal } from "./add-sales-person-popup";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedSalesPerson: any;
  isFromCar?: boolean;
  onSelectCustomer?: any;
}

const AssignCustomerModal: React.FC<Props> = ({
  open,
  onClose,
  selectedSalesPerson,
  isFromCar = false,
  onSelectCustomer,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [openAddCustomer, setOpenAddCustomer] = useState(false);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) =>
      prev?.includes(id) ? prev?.filter((item) => item !== id) : [...prev, id]
    );
  };

  const { data } = useRequestState({
    stateSelector: selectCustomerState,
  });

  const { loading } = useRequestState({
    stateSelector: selectAssignCustomerToSalesPerson,
    errorShown: true,
    successMessageShown: true,
  });

  const onSubmit = () => {
    if (isFromCar) {
    } else {
      if (selectedSalesPerson && selectedIds.length > 0) {
        dispatch(
          assignCustomerToSalesPerson({
            customerIds: selectedIds,
            salespersonId: selectedSalesPerson?.id,
          })
        );
        onClose();
      }
    }
  };
  // useEffect(() => {
  //   dispatch(salesPerson({ role: "User" }));
  // }, [dispatch]);
  useEffect(() => {
    dispatch(customer("Customer"));
    setSelectedIds([0]);
  }, [dispatch]);

  const filteredCustomers = (data || [])?.filter((customer: any) =>
    customer?.firstName?.toLowerCase()?.includes(searchText?.toLowerCase())
  );
  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pr: 2, pt: 1 }}
      >
        <DialogTitle
          sx={{
            flex: 1,
            fontFamily: fontFamily,
            fontWeight: "500",
            color: "#000000",
          }}
        >
          {Strings.button.selectCustomer}
        </DialogTitle>
        <Icon
          icon="material-symbols-light:close"
          width={24}
          onClick={onClose}
          style={{ cursor: "pointer" }}
        />
      </Box>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          sx={{
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button
            variant="contained"
            size="medium"
            sx={{
              width: { xs: "100%", md: 300 },
            }}
            onClick={(e) => {
              setOpenAddCustomer(true);
            }}
          >
            {Strings.button.addCustomer}
          </Button>
        </Stack>

        <List
          sx={{
            maxHeight: "40vh",
            overflowY: "auto",
          }}
        >
          {filteredCustomers?.map((customer: any) => (
            <ListItem
              key={customer.id}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                mb: 1,
              }}
              onClick={() => {
                onSelectCustomer && onSelectCustomer(customer);
              }}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectedIds.includes(customer?.id)}
                  onChange={() => handleToggle(customer?.id)}
                />
              </ListItemIcon>
              <ListItemIcon>
                {customer?.profileImage ? (
                  <img
                    src={customer?.profileImage}
                    alt={customer?.firstName}
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      border: `1px solid ${theme.palette.primary.main}`,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      border: `1px solid ${theme.palette.primary.main}`,
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: fontFamily,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    {customer?.firstName?.[0] || "?"}
                  </Box>
                )}
              </ListItemIcon>

              <ListItemText
                sx={{
                  fontFamily: fontFamily,
                  color: "#595959",
                  fontWeight: 700,
                }}
                primary={`${customer?.firstName} ${customer?.lastName}`}
                secondary={
                  <Stack spacing={0.2}>
                    <Typography
                      sx={{
                        fontFamily: fontFamily,
                        color: "#595959",
                        fontWeight: 600,
                      }}
                    >
                      {customer?.phone}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: fontFamily,
                        color: "#595959",
                        fontWeight: 600,
                      }}
                    >
                      {customer?.email}
                    </Typography>
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          px: { xs: 2, sm: 3 },
          pb: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            width: {
              xs: "100%",
              sm: "28%",
              fontFamily: fontFamily,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: 700,
            },
          }}
        >
          {Strings.button.close}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          sx={{
            width: { xs: "100%", sm: "28%" },
            color: theme.palette.common.white,
            fontFamily: fontFamily,
            fontWeight: 700,
          }}
          loading={loading}
        >
          {Strings.button.continue}
        </LoadingButton>
      </DialogActions>
      {openAddCustomer && (
        <AddSalespersonModal
          open={openAddCustomer}
          onClose={() => setOpenAddCustomer(false)}
          role={"Customer"}
          isFromCar={true}
        />
      )}
    </Dialog>
  );
};

export { AssignCustomerModal };
